import { MutationCache, onlineManager, QueryCache, QueryClient } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'
import { INTERVALS, QUERY_MAX_RETRIES } from '@/lib/constants'
import { notifyError } from '@/lib/notify'
import { ROUTES } from '@/lib/routes'

/** Código del API cuando la política exige activar la verificación en dos pasos. */
export const TWO_FACTOR_ENROLLMENT_REQUIRED_CODE = 'two_factor_enrollment_required'

/**
 * Si la política empezó a exigir el segundo factor con la sesión abierta
 * (p. ej. el administrador cambió la configuración), el API rechaza las
 * peticiones de negocio: se lleva al usuario a la activación. Devuelve
 * `true` si se manejó el error.
 */
export function redirectIfEnrollmentRequired(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.code !== TWO_FACTOR_ENROLLMENT_REQUIRED_CODE) return false
  if (window.location.pathname !== ROUTES.twoFactorSetup) window.location.assign(ROUTES.twoFactorSetup)
  return true
}

/** Metadatos opcionales de una mutación. */
declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /** `true` si la mutación muestra su propio error (p. ej. en el formulario). */
      silentError?: boolean
    }
  }
}

/**
 * Política de reintentos: nunca reintentar errores 4xx (el resultado no
 * cambiará), sí los de red y 5xx hasta `QUERY_MAX_RETRIES` veces.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.isClientError) return false
  // Sin señal y sin copia guardada: se falla ya para que la pantalla muestre
  // "Sin conexión". Reintentar sin red pausaría la consulta (cargando sin fin).
  if (error instanceof ApiError && error.isNetworkError && !onlineManager.isOnline()) return false
  return failureCount < QUERY_MAX_RETRIES
}

/** Crea el QueryClient con los valores por defecto de la app. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: INTERVALS.queryStaleMs,
        retry: shouldRetry,
        refetchOnWindowFocus: true,
        // Sin señal se intenta igual una vez: el service worker responde con la
        // copia guardada. Con el modo por omisión ('online') la consulta se
        // pausaría y la pantalla quedaría cargando para siempre; si no hay
        // copia, `shouldRetry` no reintenta y la pantalla muestra el error.
        networkMode: 'offlineFirst',
      },
      // Las escrituras nunca se pausan: o llegan, o fallan de inmediato para
      // que la pantalla las guarde en la bandeja de salida o muestre el error.
      mutations: { retry: false, networkMode: 'always' },
    },
    queryCache: new QueryCache({
      onError: (error) => {
        redirectIfEnrollmentRequired(error)
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (redirectIfEnrollmentRequired(error)) return
        // Error de validación: lo muestra el formulario junto a cada campo.
        if (error instanceof ApiError && Object.keys(error.fieldErrors).length > 0) return
        if (mutation.meta?.silentError) return
        notifyError(error)
      },
    }),
  })
}

/** Instancia única usada por la app. */
export const queryClient = createQueryClient()
